import { useMemo } from "react";
import { useSelector } from "react-redux";
import { selectState } from "../../utils/tableDataElements";

/** @typedef {'number' | 'name'} BoardCardHero */

export const BOARD_CARD_HERO = {
  NUMBER: "number",
  NAME: "name",
};

/**
 * @param {Object} [meta] Saved view metadata from the API.
 * @returns {BoardCardHero}
 */
export function getBoardCardHeroSetting(meta) {
  const hero = meta?.board_card?.hero;
  if (hero === BOARD_CARD_HERO.NAME) {
    return BOARD_CARD_HERO.NAME;
  }
  return BOARD_CARD_HERO.NUMBER;
}

/**
 * @param {Object} order Row shown on a board card.
 * @param {string} orderType Module key (e.g. shipment, purchase).
 * @param {BoardCardHero} hero Which field to emphasize in the card header.
 * @returns {string}
 */
export function resolveBoardCardHeroText(order, orderType, hero) {
  if (orderType === "shipment") {
    if (hero === BOARD_CARD_HERO.NAME) {
      return order.reference?.trim() || order.name || "—";
    }
    return order.name || "—";
  }
  if (orderType === "purchase") {
    if (hero === BOARD_CARD_HERO.NAME) {
      return order.user_reference?.trim() || order.name || "—";
    }
    return order.name || "—";
  }
  return order.name || "—";
}

/**
 * Secondary identifier for board card ETA row (swaps with the header hero).
 * @param {Object} order Row shown on a board card.
 * @param {string} orderType Module key (e.g. shipment, purchase).
 * @param {BoardCardHero} hero Which field is emphasized in the card header.
 * @returns {string}
 */
/**
 * Descriptive record name shown beside the document number (PO name, shipment reference).
 * @param {Object} order
 * @param {string} orderType
 * @returns {string}
 */
export function resolveRecordDisplayName(order, orderType) {
  if (orderType === "purchase") {
    return order.user_reference?.trim() || "";
  }
  if (orderType === "shipment") {
    return order.reference?.trim() || "";
  }
  if (orderType === "sales") {
    return order.customer_reference?.trim() || "";
  }
  return "";
}

export function resolveBoardCardFooterLabel(order, orderType, hero) {
  const nameField =
    orderType === "purchase" ? order.user_reference?.trim() : order.reference?.trim();

  if (orderType !== "shipment" && orderType !== "purchase") {
    return "";
  }
  if (hero === BOARD_CARD_HERO.NAME) {
    if (!nameField) {
      return "";
    }
    return order.name?.trim() || "";
  }
  return nameField || "";
}

/**
 * @param {{ id?: number }} selectedCurrentView Active saved view.
 * @param {string} orderType Module key.
 * @returns {BoardCardHero}
 */
export function useBoardCardHero(selectedCurrentView, orderType) {
  const completeListOfViews = useSelector(
    state => state[selectState[orderType]]?.allViews?.completeListOfViews,
  );

  return useMemo(() => {
    if (!selectedCurrentView?.id || !Array.isArray(completeListOfViews)) {
      return BOARD_CARD_HERO.NUMBER;
    }
    const view = completeListOfViews.find(item => item.id === selectedCurrentView.id);
    return getBoardCardHeroSetting(view?.meta);
  }, [completeListOfViews, selectedCurrentView?.id]);
}
